import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "../ui/button";
import { aiApi } from "./aiApi";

const REPEATS = [["sekali", "Sekali"], ["harian", "Setiap hari"], ["hari_bekerja", "Hari bekerja"], ["hujung_minggu", "Hujung minggu"],
  ["hari_tertentu", "Hari tertentu"], ["mingguan", "Setiap minggu"], ["bulanan", "Setiap bulan"], ["selang", "Selang minit"]];
const DAYS = ["isnin", "selasa", "rabu", "khamis", "jumaat", "sabtu", "ahad"];
const INP = "w-full h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-fuchsia-200";
const L = ({ children }) => <div className="text-[11px] font-bold text-slate-500 mb-1">{children}</div>;

export default function AIScheduleEditForm({ t, onSaved, onCancel }) {
  const isMsg = !!t.message;
  const [f, setF] = useState({ title: t.title || "", text: t.message || t.instruction || "", run_at: t.run_at || "", repeat: t.repeat || "sekali",
    weekdays: t.weekdays || [], interval_minutes: t.interval_minutes || 60, end_at: t.end_at || "", channels: t.channels || ["app"] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });

  const save = async () => {
    setBusy(true);
    const body = { title: f.title, run_at: f.run_at, repeat: f.repeat, channels: f.channels, end_at: f.end_at,
      [isMsg ? "message" : "instruction"]: f.text, ...(f.repeat === "hari_tertentu" ? { weekdays: f.weekdays } : {}),
      ...(f.repeat === "selang" ? { interval_minutes: Number(f.interval_minutes) } : {}) };
    try { const r = await aiApi.updateSchedule(t.id, body); toast.success(r.message); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 space-y-2.5 rounded-xl bg-fuchsia-50/50 border border-fuchsia-100 p-2.5" data-testid="ai-schedule-edit-form">
      <div><L>Tajuk</L><input value={f.title} onChange={set("title")} className={INP} data-testid="ai-schedule-edit-title" /></div>
      <div><L>{isMsg ? "Mesej peringatan" : "Arahan untuk Mimi"}</L>
        <textarea rows={2} value={f.text} onChange={set("text")} className={`${INP} h-auto py-1.5`} data-testid="ai-schedule-edit-text" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><L>Masa seterusnya (MYT)</L><input type="datetime-local" value={f.run_at} onChange={set("run_at")} className={INP} data-testid="ai-schedule-edit-time" /></div>
        <div><L>Ulangan</L>
          <select value={f.repeat} onChange={set("repeat")} className={INP} data-testid="ai-schedule-edit-repeat">
            {REPEATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
      </div>
      {f.repeat === "hari_tertentu" && (
        <div className="flex flex-wrap gap-1" data-testid="ai-schedule-edit-days">
          {DAYS.map((d) => (
            <button key={d} type="button" onClick={() => toggle("weekdays", d)} data-testid={`ai-schedule-day-${d}`}
              className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border transition-colors ${f.weekdays.includes(d) ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-slate-200 text-slate-600"}`}>{d.slice(0, 3)}</button>
          ))}
        </div>
      )}
      {f.repeat === "selang" && (
        <div><L>Setiap (minit, min 5)</L><input type="number" min={5} value={f.interval_minutes} onChange={set("interval_minutes")} className={INP} data-testid="ai-schedule-edit-interval" /></div>
      )}
      {f.repeat !== "sekali" && (
        <div><L>Tamat pada (pilihan)</L><input type="date" value={(f.end_at || "").slice(0, 10)} onChange={set("end_at")} className={INP} data-testid="ai-schedule-edit-end" /></div>
      )}
      <div className="flex items-center gap-3 text-[12px] text-slate-600">
        {["app", "whatsapp"].map((c) => (
          <label key={c} className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={f.channels.includes(c)} onChange={() => toggle("channels", c)} className="accent-fuchsia-600" data-testid={`ai-schedule-edit-ch-${c}`} />{c === "app" ? "App" : "WhatsApp"}
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={onCancel} disabled={busy} data-testid="ai-schedule-edit-cancel">Batal</Button>
        <Button size="sm" className="h-8 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs" onClick={save} disabled={busy || !f.channels.length} data-testid="ai-schedule-edit-save">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />Simpan</>}
        </Button>
      </div>
    </div>
  );
}
